/**
 * Whether the arena currently wants the whole window — the one fact the app shell needs from
 * inside a running match, and the only one.
 *
 * ── WHY THIS IS A SINGLETON AND NOT A PROP ──
 *
 * `RootLayout` draws the app bar and the beta strip *above* `<Outlet />`, so on `/play` they are
 * two rows of chrome sitting on top of the arena. The player: "the header that is above the
 * grid/map during game play should go away. The map/grid should take up the full top. We should
 * only see the header when we're in the lobby or when the player has been swallowed and the modal
 * is up."
 *
 * That is a fact about the state of a *match* — alive or dead — which lives in a `HudStore`
 * created per mount inside `GameCanvas`, several routes below the shell. Lifting it would mean a
 * context provider wrapped around the whole application to carry one boolean out of one route, or
 * a router-state read in the shell that could only ever answer "is this /play" and never "is this
 * player still alive". So it is a module singleton in `hudSettings`' shape, for `hudSettings`'
 * reason: two readers that never meet, and a value that belongs to neither of them.
 *
 * ── WHAT IT IS AND IS NOT ──
 *
 * It is **not** "am I on /play". A dead player is on `/play` with the death panel up and gets the
 * header back, because at that moment they are choosing what to do next rather than steering. It
 * is not a preference either: nothing here is stored, and it goes back to `false` when the arena
 * unmounts — a shell that stayed immersive after a route change would be an application with no
 * navigation and no way to get it back.
 *
 * **THE HEADER IS NEVER THE ONLY WAY OUT, WHICH IS WHAT MAKES HIDING IT SAFE.** The HUD's own
 * top-right zone carries Leave on every surface and no setting may take it away, the Capacitor
 * shell's back button asks before it leaves the arena, and a dead player is handed the header
 * back along with the panel. If any of those three ever stops being true, this file is the thing
 * to reconsider.
 */

/** `false` everywhere except inside a live match; see the module comment. */
let immersive = false;

const listeners = new Set<() => void>();

export function subscribeArenaChrome(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** A boolean, so `useSyncExternalStore`'s "the same value until it changes" is free. */
export function getArenaImmersive(): boolean {
  return immersive;
}

/**
 * Say whether the arena wants the window.
 *
 * Idempotent, and it notifies only on a real change — `GameCanvas` sets it from an effect that
 * re-runs on every death and every respawn, which on a busy client is several times a minute.
 */
export function setArenaImmersive(next: boolean): void {
  if (immersive === next) return;
  immersive = next;
  for (const listener of listeners) listener();
}
